// platform.roles — time-bounded role-key grants.
//
// A role's PRIVATE key is held in memory by sessions whose JWT carries
// the matching group claim. The issuer (a privileged off-browser
// process, e.g. a server function) checks Cognito group membership and
// writes a wrapped grant at:
//
//   _meta/role-keys/{role}/v{kid}/grants/{userSub}
//
// The grant blob is the role's private key, wrapped to the user's
// pubkey via the same envelope adapter the documents tier uses (so
// only that user can unwrap it). Grants carry an absolute expiry in
// their plaintext metadata.
//
// `refresh()` re-fetches all grants matching the user's group claims
// and rebuilds the in-memory cache. Revocation = the issuer stops
// minting new grants for that user. Cached keys keep working until
// expiry, then drain.
//
// THIS MODULE handles the CLIENT side: fetch + unwrap + cache. The
// issuer side (mint + write grants) would live in a privileged server
// function — for tests we simulate it with a helper that writes grants
// directly.

import {
  GetObjectCommand,
  ListObjectsV2Command,
  PutObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";
import {
  type CryptoAdapter,
  type RecipientPrivateKey,
  type RecipientPublicKey,
  createEnvelopeAdapter,
  exportPublicKeyRaw,
  importPublicKeyRaw,
} from "@s3p/s3db";

import type { IdentityModule } from "./identity.ts";

// ─────────────────────────────────────────────────────────────────────
// Wire format for the grant blob
// ─────────────────────────────────────────────────────────────────────
//
// A grant is an envelope-encrypted record. We use the SAME envelope
// adapter as the documents tier — the inner plaintext JSON is:
//
//   {
//     "kid": "<role key id>",
//     "expiresAt": "2026-05-28T00:00:00Z",
//     "rolePrivateKey": <JWK>,
//   }
//
// The envelope's recipient is the target user's pubkey. So unwrapping
// requires the user's private key (held in platform.identity), which
// neatly composes the two modules.

export interface RoleGrantPayload {
  kid: string;
  expiresAt: string; // ISO 8601
  rolePrivateKeyJwk: JsonWebKey;
}

// ─────────────────────────────────────────────────────────────────────
// Held role keys (in-memory cache)
// ─────────────────────────────────────────────────────────────────────

export interface HeldRoleKey {
  role: string;
  kid: string;
  expiresAt: Date;
  privateKey: CryptoKey;
}

// ─────────────────────────────────────────────────────────────────────
// Roles module
// ─────────────────────────────────────────────────────────────────────

export interface RolesConfig {
  getClient: () => Promise<S3Client>;
  bucket: string;
  /** The identity module providing the current user's keypair (for
   *  unwrapping grants) and ECDH primitives. */
  identity: IdentityModule;
  /** The current user's sub. Captured at construction so refresh()
   *  doesn't need it as a parameter. */
  getSub: () => string | undefined;
  /** Override the grants prefix layout. Default: _meta/role-keys/{role}/v{kid}/grants/{sub} */
  grantPath?: (args: { role: string; kid: string; sub: string }) => string;
  /** Override the role pubkey path. Default: _meta/role-keys/{role}/v{kid}/pubkey */
  rolePubkeyPath?: (args: { role: string; kid: string }) => string;
  /** Optional clock override for tests. Defaults to Date.now(). */
  now?: () => number;
}

export interface RolesModule {
  /** Re-fetch all grants the current user is entitled to. Discards
   *  expired keys from the cache. Returns the list of role keys now
   *  held. */
  refresh(): Promise<HeldRoleKey[]>;
  /** Currently-held role keys (non-expired). Synchronous — uses the
   *  most recent `refresh()` result. */
  current(): HeldRoleKey[];
  /** Get the held role keys shaped as `RecipientPrivateKey[]` for
   *  handing to the s3db envelope adapter's resolver. */
  asPrivateKeys(): RecipientPrivateKey[];
  /** Fetch a role's PUBLIC key. Needed when assembling a wrap-side
   *  recipient list "encrypt to anyone holding role X". */
  fetchRolePublicKey(role: string, kid?: string): Promise<RecipientPublicKey>;
  /** List the role kids currently published under `_meta/role-keys/{role}/`.
   *  Helper for picking the latest generation. */
  listRoleKids(role: string): Promise<string[]>;
}

const DEFAULT_GRANT_PATH = ({
  role,
  kid,
  sub,
}: {
  role: string;
  kid: string;
  sub: string;
}) => `_meta/role-keys/${role}/v${kid}/grants/${sub}`;

const DEFAULT_ROLE_PUBKEY_PATH = ({ role, kid }: { role: string; kid: string }) =>
  `_meta/role-keys/${role}/v${kid}/pubkey`;

const GRANT_INDEX_PREFIX = "_meta/role-keys/";

interface GrantsIndexEntry {
  role: string;
  kid: string;
  sub: string;
  key: string;
}

/** Parse a grant's S3 key back into (role, kid, sub). Returns null if
 *  the key doesn't match the expected shape. */
function parseGrantKey(key: string): { role: string; kid: string; sub: string } | null {
  // _meta/role-keys/{role}/v{kid}/grants/{sub}
  const m = key.match(/^_meta\/role-keys\/([^/]+)\/v([^/]+)\/grants\/(.+)$/);
  if (!m) return null;
  return { role: m[1] as string, kid: m[2] as string, sub: m[3] as string };
}

async function listAllGrantsForUser(
  s3: S3Client,
  bucket: string,
  sub: string,
): Promise<GrantsIndexEntry[]> {
  // No native "grants for sub" listing — we list under the role-keys
  // prefix and filter. For chat-scale projects this is fine; bigger
  // ones can maintain a secondary index at users/{sub}/role-grants/.
  const out: GrantsIndexEntry[] = [];
  let continuationToken: string | undefined;
  while (true) {
    const res = await s3.send(
      new ListObjectsV2Command({
        Bucket: bucket,
        Prefix: GRANT_INDEX_PREFIX,
        ContinuationToken: continuationToken,
        MaxKeys: 1000,
      }),
    );
    for (const obj of res.Contents ?? []) {
      if (!obj.Key) continue;
      const parsed = parseGrantKey(obj.Key);
      if (!parsed) continue;
      if (parsed.sub !== sub) continue;
      out.push({ ...parsed, key: obj.Key });
    }
    if (!res.IsTruncated || !res.NextContinuationToken) return out;
    continuationToken = res.NextContinuationToken;
  }
}

export function createRoles(config: RolesConfig): RolesModule {
  const grantPath = config.grantPath ?? DEFAULT_GRANT_PATH;
  const rolePubkeyPath = config.rolePubkeyPath ?? DEFAULT_ROLE_PUBKEY_PATH;
  const now = config.now ?? (() => Date.now());

  // role -> kid -> HeldRoleKey (we keep all generations the user has
  // unwrapped successfully, since older messages need older keys).
  const held = new Map<string, Map<string, HeldRoleKey>>();

  function setHeld(k: HeldRoleKey) {
    if (!held.has(k.role)) held.set(k.role, new Map());
    held.get(k.role)?.set(k.kid, k);
  }

  function pruneExpired() {
    const cutoff = now();
    for (const [, byKid] of held) {
      for (const [kid, k] of byKid) {
        if (k.expiresAt.getTime() < cutoff) byKid.delete(kid);
      }
    }
  }

  function flatten(): HeldRoleKey[] {
    pruneExpired();
    const out: HeldRoleKey[] = [];
    for (const [, byKid] of held) {
      for (const [, k] of byKid) out.push(k);
    }
    return out;
  }

  async function refresh(): Promise<HeldRoleKey[]> {
    const sub = config.getSub();
    if (!sub) throw new Error("platform.roles.refresh: not authenticated");

    const s3 = await config.getClient();
    const grants = await listAllGrantsForUser(s3, config.bucket, sub);

    // Build a temporary single-recipient adapter using the current
    // user's identity to unwrap the grants. We don't reuse the SDK's
    // crypto adapter because that one resolves recipients by prefix
    // for WRITE; here we just need UNWRAP with the user's private key.
    const myPriv = await config.identity.getMyPrivateKey(sub);
    const adapter = makeUnwrapOnlyAdapter([myPriv]);

    for (const g of grants) {
      // Skip ones we've already unwrapped successfully (cheap).
      if (held.get(g.role)?.has(g.kid)) continue;
      try {
        const cipherBytes = await getObjectBytes(s3, config.bucket, g.key);
        const plain = await adapter.unwrap(cipherBytes, g.key);
        const payload = JSON.parse(new TextDecoder().decode(plain)) as RoleGrantPayload;
        // Expiry check.
        const expiresAt = new Date(payload.expiresAt);
        if (expiresAt.getTime() <= now()) continue;
        const privateKey = await crypto.subtle.importKey(
          "jwk",
          payload.rolePrivateKeyJwk,
          { name: "ECDH", namedCurve: "P-256" },
          true,
          ["deriveBits", "deriveKey"],
        );
        setHeld({ role: g.role, kid: g.kid, expiresAt, privateKey });
      } catch {
        // Grants we can't unwrap aren't ours. Quietly skip — this is
        // the expected outcome for someone re-using a stale URL or
        // probing a grant they don't own.
      }
    }

    return flatten();
  }

  async function fetchRolePublicKey(role: string, kid?: string): Promise<RecipientPublicKey> {
    let kidToUse = kid;
    if (!kidToUse) {
      const kids = await listRoleKids(role);
      if (kids.length === 0) {
        throw new Error(`platform.roles: no published kids for role "${role}"`);
      }
      // Latest = lexicographically highest. Naming convention: monotonic.
      kidToUse = kids.sort()[kids.length - 1];
    }
    const s3 = await config.getClient();
    const bytes = await getObjectBytes(
      s3,
      config.bucket,
      rolePubkeyPath({ role, kid: kidToUse as string }),
    );
    const publicKey = await importPublicKeyRaw(bytes);
    return { kind: "role", id: role, publicKey };
  }

  async function listRoleKids(role: string): Promise<string[]> {
    const s3 = await config.getClient();
    const prefix = `_meta/role-keys/${role}/`;
    const out = new Set<string>();
    let continuationToken: string | undefined;
    while (true) {
      const res = await s3.send(
        new ListObjectsV2Command({
          Bucket: config.bucket,
          Prefix: prefix,
          ContinuationToken: continuationToken,
          MaxKeys: 1000,
        }),
      );
      for (const obj of res.Contents ?? []) {
        if (!obj.Key) continue;
        const m = obj.Key.match(/^_meta\/role-keys\/[^/]+\/v([^/]+)\//);
        if (m) out.add(m[1] as string);
      }
      if (!res.IsTruncated || !res.NextContinuationToken) break;
      continuationToken = res.NextContinuationToken;
    }
    return [...out];
  }

  function current(): HeldRoleKey[] {
    return flatten();
  }

  function asPrivateKeys(): RecipientPrivateKey[] {
    return flatten().map((k) => ({ kind: "role", id: k.role, privateKey: k.privateKey }));
  }

  return { refresh, current, asPrivateKeys, fetchRolePublicKey, listRoleKids };
}

// ─────────────────────────────────────────────────────────────────────
// Issuer side helper — for tests + an eventual privileged issuer
// ─────────────────────────────────────────────────────────────────────
//
// A real issuer would live in a privileged off-browser process (a
// server function). We expose a tiny helper that does the same work
// synchronously so unit tests can simulate "the issuer minted a grant
// for this user."

export interface IssueGrantArgs {
  s3: S3Client;
  bucket: string;
  role: string;
  kid: string;
  userSub: string;
  userPubkey: CryptoKey;
  rolePrivateKey: CryptoKey;
  expiresAt: Date;
  grantPath?: (args: { role: string; kid: string; sub: string }) => string;
}

/** Mint and PUT a single role-grant blob. Used by a (future) privileged
 *  issuer function AND by tests/fixtures that simulate issuance. */
export async function issueRoleGrant(args: IssueGrantArgs): Promise<{ key: string }> {
  const path = (args.grantPath ?? DEFAULT_GRANT_PATH)({
    role: args.role,
    kid: args.kid,
    sub: args.userSub,
  });
  // Export the role private key to JWK and bundle with expiry.
  const rolePrivateKeyJwk = await crypto.subtle.exportKey("jwk", args.rolePrivateKey);
  const payload: RoleGrantPayload = {
    kid: args.kid,
    expiresAt: args.expiresAt.toISOString(),
    rolePrivateKeyJwk,
  };
  const plain = new TextEncoder().encode(JSON.stringify(payload));

  // Wrap the payload to the user's pubkey using a single-recipient
  // envelope adapter. The grant's S3 key is its own objectKey (the
  // adapter has nowhere else to route by).
  const adapter = createEnvelopeAdapter({
    resolver: {
      recipientsFor: () => [{ kind: "user", id: args.userSub, publicKey: args.userPubkey }],
      myKeys: () => [],
    },
  });
  const cipher = await adapter.wrap(plain, path);

  await args.s3.send(
    new PutObjectCommand({
      Bucket: args.bucket,
      Key: path,
      Body: cipher,
      ContentType: "application/octet-stream",
    }),
  );
  return { key: path };
}

/** Mint and PUT the role's PUBLIC key at the well-known path so
 *  writers can encrypt to it. Idempotent. */
export async function publishRolePublicKey(args: {
  s3: S3Client;
  bucket: string;
  role: string;
  kid: string;
  rolePublicKey: CryptoKey;
  rolePubkeyPath?: (args: { role: string; kid: string }) => string;
}): Promise<{ key: string }> {
  const path = (args.rolePubkeyPath ?? DEFAULT_ROLE_PUBKEY_PATH)({
    role: args.role,
    kid: args.kid,
  });
  const raw = await exportPublicKeyRaw(args.rolePublicKey);
  await args.s3.send(
    new PutObjectCommand({
      Bucket: args.bucket,
      Key: path,
      Body: raw,
      ContentType: "application/octet-stream",
    }),
  );
  return { key: path };
}

// ─────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────

async function getObjectBytes(s3: S3Client, bucket: string, key: string): Promise<Uint8Array> {
  const res = await s3.send(new GetObjectCommand({ Bucket: bucket, Key: key }));
  return (res.Body as { transformToByteArray: () => Promise<Uint8Array> }).transformToByteArray();
}

/** A CryptoAdapter that only knows how to UNWRAP — its `recipientsFor`
 *  throws because we never call wrap on it. Used internally by `refresh`. */
function makeUnwrapOnlyAdapter(myKeys: RecipientPrivateKey[]): CryptoAdapter {
  return createEnvelopeAdapter({
    resolver: {
      recipientsFor: () => {
        throw new Error("unwrap-only adapter");
      },
      myKeys: () => myKeys,
    },
  });
}
